import React, { ReactNode, useState, useRef } from 'react';
import NextLink from 'next/link';

// Material-ui
import {
    Menu,
    MenuItem,
    Link as MaterialLink
} from '@material-ui/core';
import IconButton from '../IconButton';

// Icons
import { ContextMenu as ContextIcon } from '@/assets/icons';

// Styles
import VARIABLES from '@/assets/variables';
import './ContextMenu.scss';

type FieldAsAFunction<T> = (_item: T) => string | number;

export interface Action<T> {
    label: string | ReactNode;
    type: 'itemLink' | 'newTabLink' | 'custom';
    field?: string | FieldAsAFunction<T>;
    color?: 'error' | 'success';
    as?: string;
    href?: string;
    limit?: boolean;
    condition?: (_item: T) => boolean;
    disabled?: boolean | ((_item: T) => boolean);
    actionHandler?: (_item: T) => void;
}

interface HandleActionProps<T> {
    item: T;
    action: Action<T>;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLButtonElement>>;
}

function HandleAction<T>({
    item,
    action,
    setAnchorEl
}: HandleActionProps<T>) {
    const isDisabled = (typeof action.disabled === 'boolean' && action.disabled) || (typeof action.disabled === 'function' && action.disabled(item));
    if (!item) return;
    if (action.limit && !action.condition) throw new Error('You have to declare item condition to limit the action view');
    if (action.condition && !action.limit) throw new Error('You have to set item\'s limit to true so that the condition works');
    if (action.limit && action.condition)
        if (!action.condition(item)) return <></>;

    const materialLinkRef = useRef<HTMLSpanElement>();
    const nextLinkRef = useRef<HTMLAnchorElement>();

    const className = `ui-ContextMenu-item${isDisabled ? ' disabled' : ''}${action.color === 'error' ? ' error-color' : ''}`;
    const handleClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        e.stopPropagation();

        if (isDisabled) return;
        switch (action.type) {
            case 'newTabLink':
                materialLinkRef.current.click();
                setAnchorEl(null);
                break;

            case 'itemLink':
                nextLinkRef.current.click();
                break;

            case 'custom':
                if (!action.actionHandler) throw new Error('You have to declare an action handler in custom typed actions');
                action.actionHandler(item);
                setAnchorEl(null);
                break;
        }

        setTimeout(() => setAnchorEl(null), 0);
    };

    const actionField = !action.field
        ? null
        : typeof action.field === 'string'
            ? item[action.field]
            : action.field(item);

    return (
        <MenuItem
            className={className}
            style={isDisabled ? {
                pointerEvents: 'none',
                cursor: 'not-allowed'
            } : undefined}
            onClick={handleClick}
        >
            { action.type === 'newTabLink' && (
                <MaterialLink
                    href={actionField ? `${action.href}${actionField}` : action.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    ref={el => materialLinkRef.current = el}
                >
                    {action.label}
                </MaterialLink>
            )}
            { action.type === 'itemLink' && (
                <NextLink
                    href={(item && actionField) ? `${action.href}${actionField}` : '#'}
                    as={action.as}
                >
                    <a
                        ref={el => nextLinkRef.current = el}
                    >
                        {action.label}
                    </a>
                </NextLink>
            )}
            { action.type === 'custom' && action.label}
        </MenuItem>
    );
};

interface ContextMenuProps<T> {
    item: T;
    actions: Action<T>[];
    buttonAction?: ReactNode;
}

function ContextMenu<T>({ item, actions, buttonAction }: ContextMenuProps<T>) {
    const [ anchorEl, setAnchorEl ] = useState<HTMLButtonElement>(null);
    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton
                disableRipple
                onClick={e => {
                    // needed on tables inside cards
                    e.stopPropagation();

                    if (e.currentTarget instanceof HTMLButtonElement) {
                        setAnchorEl(e.currentTarget);
                    }
                }}
            >
                {buttonAction || (
                    <ContextIcon
                        color={VARIABLES.palette.secondary_color}
                    />
                )}
            </IconButton>
            <Menu
                classes={{
                    list: 'ui-ContextMenu',
                    paper: 'ui-ContextMenu-shadow-controller mui-fixed'
                }}
                open={open}
                anchorEl={anchorEl}
                onClose={(e: Event) => {
                    // prevent cards from opening up
                    e.stopPropagation();
                    setAnchorEl(null);
                }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                { actions.map((a, i) => (
                    <HandleAction
                        key={`ui-ContextMenu-action-${a.label}-${i + 1}`}
                        item={item}
                        action={a}
                        setAnchorEl={setAnchorEl}
                    />
                ))}
            </Menu>
        </>
    );
};

export default ContextMenu;
